import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { colors, shadows } from '../../../styles/theme/colors';
import { DialogWrapper } from './DialogWrapper';
import toggleContact from '../../../../assets/toggleContact.svg';

interface RoleSwitchDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  targetRole: 'traveller' | 'sender';
  loading?: boolean;
  primaryColor: string;
  primaryHoverColor: string;
}

const RoleSwitchDialog: React.FC<RoleSwitchDialogProps> = ({
  open,
  onClose,
  onConfirm,
  targetRole,
  loading = false,
  primaryColor,
  primaryHoverColor,
}) => {
  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const roleDisplayName = targetRole === 'traveller' ? 'Traveler' : 'Sender';

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '90%', sm: '480px' },
          maxWidth: '480px',
          height: 'auto',
          borderRadius: '12px',
          boxShadow: shadows.xl,
        },
      }}
    >
      {/* Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          padding: { xs: '24px', sm: '32px' },
        }}
      >

        {/* Icon - centered at the ripple origin (48, 48) */}
        <Box
          sx={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <Box
            component="img"
            src={toggleContact}
            alt="Role Switch"
            sx={{
              width: '32px',
              height: '32px',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: { xs: '12px', sm: '16px' },
            top: { xs: '12px', sm: '16px' },
            padding: '4px',
            borderRadius: '6px',
            zIndex: 10,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon sx={{ width: 20, height: 20, color: '#A4A7AE' }} />
        </IconButton>

        {/* Title and Description */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: { xs: '40px', sm: '48px' },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: { xs: '18px', sm: '20px' },
              lineHeight: { xs: '27px', sm: '30px' },
              color: '#181D27',
            }}
          >
            Switch to {roleDisplayName} Mode?
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: { xs: '19.5px', sm: '21px' },
              color: '#667071',
            }}
          >
            You're switching to your {roleDisplayName} account to {targetRole === 'traveller'
              ? 'create and manage new shipment requests'
              : 'accept delivery requests and manage your travel listings'}.
            <br />
            You can return to your {targetRole === 'traveller' ? 'Sender' : 'Traveler'} mode anytime.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            marginTop: '24px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {/* Cancel Button */}
          <Box
            onClick={handleClose}
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px 24px',
              height: '44px',
              background: colors.neutral.white,
              border: '1px solid #D9DADF',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: loading ? colors.neutral.white : '#F9FAFB',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: '#484A54',
              }}
            >
              Cancel
            </Typography>
          </Box>

          {/* Confirm Button */}
          <Box
            onClick={handleConfirm}
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px 24px',
              height: '44px',
              background: loading ? primaryColor : primaryColor,
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.8 : 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: loading ? primaryColor : primaryHoverColor,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: colors.neutral.white }} />
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: colors.neutral.white,
                }}
              >
                Switch Now
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </DialogWrapper>
  );
};

export default RoleSwitchDialog;
